import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import cs from './locales/cs.json'
import de from './locales/de.json'
import en from './locales/en.json'
import hu from './locales/hu.json'
import pl from './locales/pl.json'
import sk from './locales/sk.json'
import uk from './locales/uk.json'

import { DEFAULT_LANGUAGE, LANGUAGE_CATALOG, type LanguageCode } from './language-catalog'
import { setTranslationLanguage } from './translation-memory'

const STORED_LANGUAGE_KEY = 'fk_lang'
const STORED_USER_KEY = 'fk_user'

/**
 * The bundles ported from legacy. The catalog lists twelve languages; seven
 * have curated content and the rest resolve through the fallback chain until
 * somebody writes them — adding a JSON later just works, exactly as legacy
 * documents.
 */
export const RESOURCES = {
  sk: { translation: sk },
  cs: { translation: cs },
  en: { translation: en },
  hu: { translation: hu },
  de: { translation: de },
  pl: { translation: pl },
  uk: { translation: uk },
  ro: { translation: {} },
  it: { translation: {} },
  fr: { translation: {} },
  es: { translation: {} },
  ru: { translation: {} },
} as const

function readStored(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Explicit choice, then the cached user's preference, then Slovak.
 *
 * **Never `navigator.language`** — ported comment and all. Staff on an
 * English-locale browser speak Slovak, and inferring from the browser produced
 * a half-translated UI. The switcher in the profile is the only way to opt out
 * of Slovak.
 */
function initialLanguage(): LanguageCode {
  const stored = readStored(STORED_LANGUAGE_KEY)
  if (stored && stored in RESOURCES) return stored as LanguageCode

  try {
    const cachedUser = readStored(STORED_USER_KEY)
    if (cachedUser) {
      const { preferredLanguage } = JSON.parse(cachedUser) as { preferredLanguage?: string }
      if (preferredLanguage && preferredLanguage in RESOURCES) {
        return preferredLanguage as LanguageCode
      }
    }
  } catch {
    // A corrupt cached user must not stop the app from choosing a language.
  }

  return DEFAULT_LANGUAGE
}

let initialised = false

export function initI18n(): typeof i18next {
  if (initialised) return i18next
  initialised = true

  void i18next.use(initReactI18next).init({
    resources: RESOURCES,
    lng: initialLanguage(),
    fallbackLng: ['sk', 'en'],
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  })

  // The static bundles and the API-backed memory follow the same language.
  setTranslationLanguage(i18next.language)
  i18next.on('languageChanged', (language: string) => setTranslationLanguage(language))

  return i18next
}

/** Persist the chosen language and apply it to both mechanisms. */
export function setLanguage(code: string): void {
  if (!(code in RESOURCES)) return
  try {
    localStorage.setItem(STORED_LANGUAGE_KEY, code)
  } catch {
    // A kiosk with storage disabled still switches for this session.
  }
  void i18next.changeLanguage(code)
}

export { LANGUAGE_CATALOG, DEFAULT_LANGUAGE, type LanguageCode }
export default i18next
